import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle2, XCircle } from 'lucide-react';
import {
  useCampaigns,
  useCreateBooking,
  useCreateCampaign,
  useRateCard,
  useAvailability,
} from '@/api/ads/hooks';
import { extractErrorMessage } from '@/api/client';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardHeader, CardBody } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label, Select } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { npr, titleCase, toISODate } from '@/lib/format';

export function BookAdsPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const rateCard = useRateCard();
  const campaigns = useCampaigns();
  const createCampaign = useCreateCampaign();
  const createBooking = useCreateBooking();

  const today = new Date();
  const in7 = new Date(Date.now() + 7 * 86400000);

  const [campaignId, setCampaignId] = useState<string>(params.get('campaign') ?? '');
  const [newCampaignName, setNewCampaignName] = useState('');
  const [productId, setProductId] = useState<string>('');
  const [daypartId, setDaypartId] = useState<string>('');
  const [start, setStart] = useState(toISODate(today));
  const [end, setEnd] = useState(toISODate(in7));
  const [slots, setSlots] = useState(1);

  const product = useMemo(
    () => rateCard.data?.find((p) => String(p.id) === productId),
    [rateCard.data, productId],
  );
  const dayparts = useMemo(() => {
    const seen = new Map<number, string>();
    product?.rates.forEach((r) => {
      if (r.daypart && r.daypart_label) seen.set(r.daypart, r.daypart_label);
    });
    return [...seen.entries()].map(([id, label]) => ({ id, label }));
  }, [product]);

  const availability = useAvailability({
    product: product?.id,
    daypart: daypartId ? Number(daypartId) : null,
    start,
    end,
    slots,
  });

  const needsDaypart = !!product?.requires_daypart;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!product) return toast.error('Choose an ad product.');
    if (needsDaypart && !daypartId) return toast.error('Choose a daypart.');

    let campaign = campaignId ? Number(campaignId) : 0;
    try {
      if (!campaign) {
        if (!newCampaignName.trim()) return toast.error('Name your new campaign.');
        const created = await createCampaign.mutateAsync({ name: newCampaignName.trim() });
        campaign = created.id;
      }
      const booking = await createBooking.mutateAsync({
        campaign,
        product: product.id,
        daypart: daypartId ? Number(daypartId) : null,
        start_date: start,
        end_date: end,
        slots_requested: slots,
      });
      toast.success('Booking created. Add your creative and submit for review.');
      navigate(`/bookings/${booking.id}`);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  if (rateCard.isLoading) return <Spinner label="Loading rate card…" />;

  return (
    <>
      <PageHeader title="Book an ad" description="Reserve a placement from the Fasto rate card." />
      <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
        <Card>
          <CardHeader title="Placement details" />
          <CardBody>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label>Campaign</Label>
                <Select value={campaignId} onChange={(e) => setCampaignId(e.target.value)}>
                  <option value="">➕ Create new campaign</option>
                  {campaigns.data?.results.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              </div>
              {!campaignId && (
                <div>
                  <Label>New campaign name</Label>
                  <Input value={newCampaignName} onChange={(e) => setNewCampaignName(e.target.value)} placeholder="e.g. Tihar Blast 2026" />
                </div>
              )}

              <div>
                <Label>Ad product</Label>
                <Select
                  value={productId}
                  onChange={(e) => {
                    setProductId(e.target.value);
                    setDaypartId('');
                  }}
                  required
                >
                  <option value="">Select a product…</option>
                  {rateCard.data?.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} · {titleCase(p.billing_unit)}
                    </option>
                  ))}
                </Select>
                {product?.description && <p className="mt-1 text-xs text-muted-foreground">{product.description}</p>}
              </div>

              {needsDaypart && (
                <div>
                  <Label>Daypart</Label>
                  <Select value={daypartId} onChange={(e) => setDaypartId(e.target.value)} required>
                    <option value="">Select a daypart…</option>
                    {dayparts.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.label}
                      </option>
                    ))}
                  </Select>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Start date</Label>
                  <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} required />
                </div>
                <div>
                  <Label>End date</Label>
                  <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} required />
                </div>
              </div>

              <div>
                <Label>Slots</Label>
                <Input
                  type="number"
                  min={1}
                  value={slots}
                  onChange={(e) => setSlots(Math.max(1, Number(e.target.value)))}
                />
              </div>

              <Button type="submit" size="lg" className="w-full" loading={createBooking.isPending || createCampaign.isPending}>
                Create booking
              </Button>
            </form>
          </CardBody>
        </Card>

        <Card className="h-fit">
          <CardHeader title="Availability & price" description="Live quote from the rate card." />
          <CardBody>
            {!product ? (
              <p className="text-sm text-muted-foreground">Select a product to see pricing.</p>
            ) : availability.isFetching ? (
              <Spinner label="Checking availability…" />
            ) : availability.isError ? (
              <p className="text-sm text-danger">No active rate for this selection.</p>
            ) : availability.data ? (
              <div className="space-y-3 text-sm">
                <Row label="Unit" value={titleCase(availability.data.unit)} />
                <Row label="Unit price" value={npr(availability.data.unit_price)} />
                <Row label="Capacity" value={String(availability.data.capacity)} />
                <Row
                  label="Available slots"
                  value={
                    <span className={availability.data.can_book ? 'text-success' : 'text-danger'}>
                      {availability.data.available}
                    </span>
                  }
                />
                {availability.data.implied_cpm && (
                  <Row label="Implied CPM" value={npr(availability.data.implied_cpm)} />
                )}
                <div className="border-t pt-3">
                  <Row
                    label={<span className="font-semibold text-foreground">Total</span>}
                    value={<span className="text-lg font-semibold">{npr(availability.data.total_amount)}</span>}
                  />
                </div>
                <div
                  className={`flex items-center gap-2 rounded-md px-3 py-2 text-xs ${
                    availability.data.can_book
                      ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400'
                      : 'bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-400'
                  }`}
                >
                  {availability.data.can_book ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                  {availability.data.can_book
                    ? 'Inventory available for these dates.'
                    : 'Not enough inventory — try fewer slots or different dates.'}
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Enter dates to see a quote.</p>
            )}
          </CardBody>
        </Card>
      </div>
    </>
  );
}

function Row({ label, value }: { label: React.ReactNode; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span>{value}</span>
    </div>
  );
}
